import Axios, { AxiosError, AxiosRequestConfig, RawAxiosRequestHeaders } from "axios"
import ClientToken from "common/clientToken"
// eslint-disable-next-line no-restricted-imports
import Logger from "common/Logger"
import { store } from "components/protest.store"
import get from "lodash/get"
import { DateTime } from "luxon"
import qs from "qs"

import { getErrorMessage } from "./Errors"

const LOGGER = new Logger("http.ts")
export interface RequestConfig {
    headers?: RawAxiosRequestHeaders
    params?: Record<string, unknown>
    responseType?: "arraybuffer" | "blob" | "document" | "json" | "text" | "stream"
    /** Optional per-request timeout in ms. Left unset (no timeout) unless a caller opts in. */
    timeout?: number
}

export interface HttpError {
    errorMessage: string
    statusCode: number
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    record: any
    headers?: Record<string, string | string[]>
}

/**
 * @param error the object to check
 * @returns true if is an instance of HttpError
 */
export function isHttpError(error: unknown): error is HttpError {
    return get(error, "errorMessage") && get(error, "statusCode") ? true : false
}

/**
 * True when the request never reached the server (connection dropped, no response received),
 * as opposed to the server responding with an actual error status. Used to decide whether to
 * fall back to the local SQLite cache, since store.state.isAppOffline can lag behind an
 * in-flight request actually failing - checking the error shape itself avoids that race.
 *
 * @param error the object to check
 */
export function isNetworkError(error: unknown): boolean {
    return isHttpError(error) && error.errorMessage === "No response from server"
}

/**
 *
 * @param error
 */
export function isAxiosError(error: unknown): error is AxiosError<any, any> {
    return (error as any).isAxiosError
}

/**
 * @param config an axios request config (or config-shaped object) that may carry an Authorization/Cookie header
 * @returns a shallow copy of config safe to pass to the logger, with sensitive headers redacted
 */
const redactConfigForLogging = (config: unknown): unknown => {
    if (!config || typeof config !== "object") return config

    const headers = get(config, "headers")
    if (!headers || typeof headers !== "object") return config

    const redactedHeaders: Record<string, unknown> = { ...headers }
    for (const key of Object.keys(redactedHeaders)) if (["authorization", "cookie"].includes(key.toLowerCase())) redactedHeaders[key] = "[REDACTED]"

    return { ...config, headers: redactedHeaders }
}

/**
 * @param headers the existing header object
 * @returns a modified header object that includes the Authorization: Bearer token
 */
const appendAuthAndTimezoneToHeaders = (headers?: RawAxiosRequestHeaders): RawAxiosRequestHeaders | undefined => {
    const token = new ClientToken().get()
    const timezoneOfUser = DateTime.local().zone.name

    if (!headers) headers = {}

    if (!headers.Authorization && token) headers.Authorization = `Bearer ${token}`

    if (!headers["User-Timezone"]) headers["User-Timezone"] = timezoneOfUser

    return headers
}

/**
 * @param url the url to make the request to
 * @param method the method to use for the request
 * @param [data] optional data to send in the request body
 * @param [config] optional configuration object to use (headers, query params, responseType)
 * @returns AxiosRequestConfig
 */
const createRequestParams = (
    url: string,
    method: "put" | "get" | "post" | "patch" | "delete",
    data?: unknown,
    config?: RequestConfig
): AxiosRequestConfig => {
    const axiosConfig: AxiosRequestConfig = {
        url,
        method,
        data
    }

    if (config) {
        axiosConfig.params = config.params
        axiosConfig.headers = config.headers
        axiosConfig.responseType = config.responseType
        axiosConfig.timeout = config.timeout

        // In the mobile build, "axios" is aliased to redaxios (see webpack.mobile.js), whose fetch-based
        // implementation looks up the response body reader as res[responseType](), i.e. res.arrayBuffer()
        // (camelCase, matching the Fetch API) rather than axios's res["arraybuffer"]. Passing axios's
        // spelling through unchanged means redaxios can't find the method and throws.
        if (LOGLABEL === "client.mobile" && config.responseType === "arraybuffer") {
            ;(axiosConfig as { responseType: string }).responseType = "arrayBuffer"
        }
    }

    axiosConfig.maxBodyLength = 100000000
    axiosConfig.maxContentLength = 100000000

    axiosConfig.headers = appendAuthAndTimezoneToHeaders(axiosConfig.headers)
    axiosConfig.paramsSerializer = {
        serialize: (params) => {
            // @ts-ignore the allowEmptyArrays exists
            return qs.stringify(params, { arrayFormat: "brackets", allowEmptyArrays: true })
        }
    }

    return axiosConfig
}

/**
 * @param url the url to make the request to
 * @param method the method to use for the request
 * @param [data] optional data to send in the request body
 * @param [config] optional configuration object to use (headers, query params, responseType)
 * @param [base64] optional boolean to indicate if the response should be base64
 * @returns the response or an HttpError
 */
async function makeAxiosCall<T>(
    url: string,
    method: "put" | "get" | "post" | "patch" | "delete",
    data?: unknown,
    config?: RequestConfig,
    base64?: boolean
): Promise<T | HttpError> {
    try {
        const response = await Axios.request(createRequestParams(url, method, data, config))
        if (base64) {
            try {
                const dataType = response.headers["content-type"]
                if (!dataType) {
                    return {
                        errorMessage: "Failed get base64 version of record",
                        statusCode: 500,
                        record: undefined
                    }
                }

                const base64String = Buffer.from(response.data, "binary").toString("base64")
                if (base64String.trim().length === 0) {
                    return {
                        errorMessage: "Zero byte file",
                        statusCode: 500,
                        record: undefined
                    }
                }

                return `data:${dataType};base64,${base64String}` as unknown as T
            } catch (error) {
                const message = getErrorMessage(error)
                LOGGER.error("Failed to convert buffer to base64", { error: { message } })
                return {
                    errorMessage: "Failed to convert buffer to base64",
                    statusCode: 500,
                    record: undefined
                }
            }
        }

        return response.data
    } catch (error) {
        let sessionId
        if (config && config.headers && config.headers.Authorization) {
            const auth = config.headers.Authorization
            if (typeof auth === "string") sessionId = auth.split(" ").pop()
        }

        if (isAxiosError(error)) {
            // Error
            if (error.response) {
                // The request was made and the server responded with a status code
                // that falls out of the range of 2xx
                LOGGER.debug(
                    "Recieved error from server",
                    {
                        status: error.response.status,
                        data: error.response.data,
                        headers: error.response.headers,
                        config: redactConfigForLogging(error.config)
                    },
                    sessionId
                )

                if (error.response.status === 401 || error.response.status === 403) {
                    // Only trigger if we actually had a token (i.e. this isn't a login attempt)
                    const hadToken = new ClientToken().get()
                    if (hadToken) {
                        new ClientToken().remove()
                        store.commit("clearState")
                        store.commit("clearWeatherCache")
                        store.commit("setForceShowAuth", true)
                    }
                }

                return {
                    statusCode: get(error, "response.status") || 500,
                    errorMessage: get(error, "response.data") || "Unknown error occurred",
                    record: undefined
                }
            } else if (error.request) {
                // The request was made but no response was received
                // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
                // http.ClientRequest in node.js
                LOGGER.error(
                    "Request made no response from server",
                    {
                        request: error.request,
                        config: redactConfigForLogging(error.config)
                    },
                    sessionId
                )

                return {
                    errorMessage: "No response from server",
                    statusCode: 500,
                    record: undefined
                }
            } else {
                // Something happened in setting up the request that triggered an Error
                LOGGER.error(
                    "Unknown error occurred during request",
                    {
                        message: error.message,
                        config: redactConfigForLogging(error.config)
                    },
                    sessionId
                )

                return {
                    errorMessage: error.message || "Unknown error occurred",
                    statusCode: 500,
                    record: undefined
                }
            }
        } else {
            const status = get(error, "status")
            const statusText = get(error, "statusText")
            const url = get(error, "url") || get(error, "config.url")

            LOGGER.error(
                "Non-Axios error shape during request",
                { status, statusText, url, config: redactConfigForLogging(get(error, "config")) },
                sessionId
            )

            return {
                errorMessage: status !== undefined ? `Request failed: ${status}${statusText ? ` ${statusText}` : ""}` : getErrorMessage(error),
                statusCode: typeof status === "number" ? status : 500,
                record: undefined
            }
        }
    }
}

/**
 * Makes auth header
 *
 * @param [sessionId] optional sessionId
 * @returns auth header
 */
export function makeAuthHeader(sessionId?: string): RawAxiosRequestHeaders {
    return sessionId ? { Authorization: "Bearer " + sessionId } : {}
}

/**
 * Makes params
 *
 * @param [params] optional params object
 * @returns params
 */
export function makeParams(params?: Record<string, unknown>): Record<string, unknown> {
    return params || {}
}

const http = {
    get: <T>(url: string, config?: RequestConfig, base64?: boolean): Promise<T | HttpError> => {
        if (!config) config = {}

        if (base64) config.responseType = "arraybuffer"

        return makeAxiosCall<T>(url, "get", undefined, config, base64)
    },
    put: <T>(url: string, data: unknown, config?: RequestConfig): Promise<T | HttpError> => {
        return makeAxiosCall<T>(url, "put", data, config)
    },
    post: <T>(url: string, data: unknown, config?: RequestConfig): Promise<T | HttpError> => {
        return makeAxiosCall<T>(url, "post", data, config)
    },
    patch: <T>(url: string, data: unknown, config?: RequestConfig): Promise<T | HttpError> => {
        return makeAxiosCall<T>(url, "patch", data, config)
    },
    delete: <T>(url: string, config?: RequestConfig): Promise<T | HttpError> => {
        return makeAxiosCall<T>(url, "delete", undefined, config)
    }
}

export default http
